import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { ResourceProvider } from "@/Common/Resources/StaticResources";
import { Button } from "@/DesignSystem";
import { Label } from "@/DesignSystem/Molecules/ToolBox/Label";
import { NewExperiencePasswordBoxControl } from "@/Myle/EntryPoints/MyleApp/Login/NewExperiencePasswordBoxControl";

import styles from "./NewExperienceLoginCredentialsView.module.scss";

export interface INewExperienceLoginCredentialsViewProps {
    userName: string;
    validatingCredentials: boolean;
    isLoggedIn: boolean;
    stringProvider: ResourceProvider;
    errorMessageJsx: JSX.Element;
    buttonTextLabelKey: string;
    isLoading: boolean;
    isUserRelogin?: boolean;

    onSubmit: () => void;
    onUsernameChange: (username: string) => void;
    onPasswordChange: (password: string) => void;
}

export function NewExperienceLoginCredentialsView(props: Readonly<INewExperienceLoginCredentialsViewProps>): JSX.Element {
    const { errorMessageJsx, userName, validatingCredentials, isLoggedIn, stringProvider, buttonTextLabelKey, isUserRelogin, isLoading } = props;
    const { getText } = useTranslation(stringProvider);

    function handleKeyPress(e: React.KeyboardEvent): void {
        if (!validatingCredentials && e.key === "Enter")
            enterKeyPress();
    }

    function enterKeyPress(): void {
        props.onSubmit();
    }

    return (
        <>
            <div className={styles.loginRow}>
                <Label htmlFor="user_name" label={getText("username")} />
                <input
                    type="text"
                    id="user_name"
                    data-cy="NewExperienceLogin-username"
                    disabled={validatingCredentials || isUserRelogin || isLoading}
                    className="form-control"
                    placeholder={getText("UsernamePlaceholder")}
                    onKeyUp={e => handleKeyPress(e)}
                    onChange={e => props.onUsernameChange(e.target.value)}
                    value={userName}
                />
            </div>
            {!validatingCredentials && (!isLoggedIn || isUserRelogin) && (
                <div className={styles.loginRow}>
                    <div className={styles.passwordLabelContainer}>
                        <div className={styles.passwordLabel}>
                            <Label
                                htmlFor="password"
                                label={getText("password")}
                            />
                        </div>
                    </div>
                    <NewExperiencePasswordBoxControl
                        id="password"
                        dataCy="NewExperienceLogin-password"
                        className="form-control"
                        placeholder={getText("PasswordPlaceholder")}
                        capsLockOnMessage={getText("CapsLockOn")}
                        onChange={value => props.onPasswordChange(value)}
                        onEnterPress={() => enterKeyPress()}
                    />
                </div>
            )}
            <div>{errorMessageJsx}</div>
            <div className={styles.loginButtonContainer}>
                <Button
                    variant="brand"
                    disabled={props.validatingCredentials || (isLoggedIn && !isUserRelogin)}
                    dataCy="NewExperience-login-button"
                    onClick={() => props.onSubmit()}
                    label={getText(buttonTextLabelKey)}
                    size="lg"
                    id="loginButton"
                />
            </div>
        </>
    );
}
